/**
 * Enhanced Menu Drawer Animations
 * Provides smooth slide animations for mobile and tablet navigation
 */

class EnhancedMenuDrawer {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupAnimations());
    } else {
      this.setupAnimations();
    }
  }

  setupAnimations() {
    const menuDrawer = document.querySelector('menu-drawer');
    if (!menuDrawer) return;

    const detailsElement = menuDrawer.querySelector('details');
    if (!detailsElement) return;

    // Enhanced opening animation
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          const isOpening = detailsElement.hasAttribute('open');
          
          if (isOpening) {
            this.animateMenuOpen(menuDrawer);
          } else {
            this.animateMenuClose(menuDrawer);
          }
        }
      });
    });

    observer.observe(detailsElement, { attributes: true });

    // Add touch gesture support for mobile
    this.addTouchGestures(menuDrawer);
  }

  animateMenuOpen(menuDrawer) {
    const drawer = menuDrawer.querySelector('.menu-drawer');
    const menuItems = menuDrawer.querySelectorAll('.menu-drawer__menu li');
    
    // Reset menu items for animation
    menuItems.forEach((item, index) => {
      item.style.opacity = '0';
      item.style.transform = 'translateX(-20px)';
      item.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      item.style.transitionDelay = `${0.1 + (index * 0.05)}s`;
    });

    // Trigger staggered animation
    requestAnimationFrame(() => {
      menuItems.forEach((item) => {
        item.style.opacity = '1';
        item.style.transform = 'translateX(0)';
      });
    });

    // Add subtle bounce effect for mobile
    if (window.innerWidth <= 990) {
      drawer.style.animation = 'slideInBounce 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    }
  }

  animateMenuClose(menuDrawer) {
    const menuItems = menuDrawer.querySelectorAll('.menu-drawer__menu li');
    
    // Reverse animation for closing
    menuItems.forEach((item, index) => {
      item.style.transitionDelay = `${index * 0.02}s`;
      item.style.opacity = '0';
      item.style.transform = 'translateX(-10px)';
    });
  }

  addTouchGestures(menuDrawer) {
    const drawer = menuDrawer.querySelector('.menu-drawer');
    if (!drawer) return;

    let startX = 0;
    let currentX = 0;
    let isDragging = false;

    drawer.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      isDragging = true;
      drawer.style.transition = 'none';
    }, { passive: true });

    drawer.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      
      currentX = e.touches[0].clientX;
      const deltaX = currentX - startX;
      
      // Only allow left swipe (closing gesture)
      if (deltaX < 0) {
        const translateX = Math.max(deltaX, -drawer.offsetWidth);
        drawer.style.transform = `translateX(${translateX}px)`;
      }
    }, { passive: true });

    drawer.addEventListener('touchend', () => {
      if (!isDragging) return;
      
      isDragging = false;
      drawer.style.transition = '';
      
      const deltaX = currentX - startX;
      
      // Close if swiped more than 30% of drawer width
      if (deltaX < -drawer.offsetWidth * 0.3) {
        const detailsElement = menuDrawer.querySelector('details');
        if (detailsElement) {
          detailsElement.removeAttribute('open');
        }
      } else {
        // Snap back to open position
        drawer.style.transform = 'translateX(0)';
      }
    }, { passive: true });
  }
}

// Add CSS keyframes for bounce animation
const style = document.createElement('style');
style.textContent = `
  @keyframes slideInBounce {
    0% {
      transform: translateX(-100%) scale(0.95);
      opacity: 0;
    }
    60% {
      transform: translateX(2%) scale(1.02);
      opacity: 1;
    }
    100% {
      transform: translateX(0) scale(1);
      opacity: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .menu-drawer {
      animation: none !important;
    }
    
    .menu-drawer__menu li {
      transition-delay: 0s !important;
    }
  }
`;
document.head.appendChild(style);

// Initialize enhanced menu drawer
new EnhancedMenuDrawer();